import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock, Unlock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (p) => p >= 1_000_000 ? `$${(p / 1_000_000).toFixed(2)}M` : `$${p.toLocaleString()}`;

export default function Marketplace() {
  const [sectors, setSectors] = useState([]);
  const [listings, setListings] = useState([]);
  const [teaseSeconds] = useState(10);

  useEffect(() => {
    axios.get(`${API}/marketplace/sectors`).then((r) => setSectors(r.data.sectors)).catch(() => {});
    axios.get(`${API}/marketplace/listings`).then((r) => setListings(r.data.listings)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Sector Selector */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12">
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Marketplace Bridge</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
              Four sectors.<br />
              <span className="font-display italic text-[#D4AF37]">One vessel.</span>
            </h1>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
              Real Estate is open. The other three sectors are reserved for future Lineage Law cohorts.
              Each listing is gated by a 10-second tease — full dossier disclosed only after boarding.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {sectors.map((s) => (
            <div
              key={s.key}
              data-testid={`sector-${s.key}`}
              className={`sov-border p-6 bg-[#0A0A0A] ${s.status === 'Active' ? 'border-[#D4AF37]/50' : 'opacity-50'}`}
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4">{s.status}</div>
              <h3 className="text-xl font-light tracking-tight mb-3">{s.name}</h3>
              <p className="text-xs text-[#A3A3A3] font-light leading-relaxed mb-4 min-h-[40px]">{s.tagline}</p>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                {s.listings_count > 0 ? `${s.listings_count} listings` : 'Coming Soon'}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Real Estate Tease & Lock */}
      <section className="bg-[#0A0A0A] border-t border-b border-[#D4AF37]/15">
        <div className="px-6 md:px-12 lg:px-24 py-16">
          <div className="flex items-center justify-between mb-10 border-b border-[#D4AF37]/15 pb-5">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Sector · Real Estate</div>
              <div className="text-2xl md:text-3xl font-light tracking-tight">10-Second Tease & Lock</div>
            </div>
            <Link to="/boarding" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] hover:text-white transition-colors">
              Unlock via Boarding <Unlock size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {listings.map((l) => (
              <TeaseLockCard key={l.id} listing={l} teaseSeconds={teaseSeconds} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const TeaseLockCard = ({ listing, teaseSeconds }) => {
  const [secondsLeft, setSecondsLeft] = useState(teaseSeconds);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (locked) return;
    if (secondsLeft <= 0) { setLocked(true); return; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, locked]);

  return (
    <div data-testid={`tease-${listing.id}`} className="relative sov-border bg-[#050505] overflow-hidden group">
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={listing.hero_image}
          alt={listing.title}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${locked ? 'blur-2xl scale-110' : ''}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>

        {/* Tease badge */}
        {!locked && (
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] uppercase bg-black/60 backdrop-blur-sm px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse"></span>
              Tease · {secondsLeft}s
            </span>
            <span className="text-[10px] tracking-[0.3em] uppercase bg-black/60 backdrop-blur-sm px-3 py-1.5 text-[#F5F5F5]">{listing.country}</span>
          </div>
        )}

        {/* Bottom card content during tease */}
        {!locked && (
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">{listing.region}</div>
            <h3 className="text-2xl font-light leading-tight text-white mb-3">{listing.title}</h3>
            <div className="flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
              <span>{listing.bedrooms} Bd</span>
              <span className="w-1 h-1 bg-[#D4AF37]"></span>
              <span>{listing.bathrooms} Ba</span>
              <span className="w-1 h-1 bg-[#D4AF37]"></span>
              <span>{listing.area_sqft.toLocaleString()} sqft</span>
            </div>
          </div>
        )}

        {/* Lock overlay */}
        {locked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm p-6 text-center">
            <Lock size={28} className="text-[#D4AF37] mb-4" strokeWidth={1} />
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Sealed</div>
            <p className="text-sm text-[#F5F5F5] font-light leading-relaxed mb-6 max-w-xs">
              Full dossier disclosed under admiral seal. Begin boarding to unlock.
            </p>
            <Link to="/boarding" className="btn-sovereign">
              Unlock <Unlock size={14} strokeWidth={1.5} />
            </Link>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-[#D4AF37]/15 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">{locked ? 'Valuation' : 'Asking'}</div>
          <div className={`font-display text-xl text-[#D4AF37] ${locked ? 'blur-sm select-none' : ''}`}>{fmt(listing.price_usd)}</div>
        </div>
        {!locked ? (
          <Link to={`/vault/${listing.id}`} className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] flex items-center gap-2">
            Dossier <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        ) : (
          <Link to="/boarding" className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] flex items-center gap-2">
            Board <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        )}
      </div>
    </div>
  );
};
